/**
 * The fitted timber frame of the older house by the produce market (lifted
 * from lantern-water's `timber-house.js`): sills and posts around the upper
 * windows, pegged joints, curved knee braces (the mirrored contour baked with
 * its own winding, so batching keeps it), diagonal members, exposed joist
 * ends, and a fan truss on the visible return gable. Built at the house's
 * (x, z) in `parent`'s space.
 */
import * as THREE from 'three';
import type { Ctx } from './kit.ts';

export const timberHouse = (ctx: Ctx, parent: THREE.Object3D, x: number, z: number, width: number, depth: number, height: number): THREE.Group => {
  const m = ctx.m;
  const box = (...a: Parameters<Ctx['box']>): ReturnType<Ctx['box']> => ctx.box(...a);
  const mesh = (...a: Parameters<Ctx['mesh']>): ReturnType<Ctx['mesh']> => ctx.mesh(...a);
  const line = (...a: Parameters<Ctx['line']>): ReturnType<Ctx['line']> => ctx.line(...a);
  const root = new THREE.Group();
  root.name = 'market-house-timber-frame';
  parent.add(root);
  const bottom = height * 0.54;
  const top = height - 0.06;
  const knee = new THREE.Shape();
  knee.moveTo(0, 0);
  knee.quadraticCurveTo(0.23, 0.59, 0.7, 0.88);
  knee.lineTo(0.59, 0.96);
  knee.quadraticCurveTo(0.13, 0.76, -0.1, 0.055);
  knee.closePath();
  const kneeGeometry = new THREE.ExtrudeGeometry(knee, { depth: 0.13, bevelEnabled: true, bevelSize: 0.008, bevelThickness: 0.008, bevelSegments: 1, curveSegments: 8 });
  const reverseKnee = new THREE.ExtrudeGeometry(new THREE.Shape(knee.getPoints(8).map((p) => new THREE.Vector2(-p.x, p.y))), {
    depth: 0.13,
    bevelEnabled: true,
    bevelSize: 0.008,
    bevelThickness: 0.008,
    bevelSegments: 1,
    curveSegments: 1,
  });
  const face = (cx: number, cz: number, length: number, angle: number, innerPost: number, endPost: number): THREE.Group => {
    const group = new THREE.Group();
    group.position.set(cx, 0, cz);
    group.rotation.y = angle;
    root.add(group);
    box(group, 0, bottom + 0.08, 0.14, length, 0.19, 0.22, m.darkWood);
    box(group, 0, top, 0.13, length + 0.05, 0.18, 0.22, m.darkWood);
    for (const xx of [-endPost, -innerPost, 0, innerPost, endPost]) {
      box(group, xx, (bottom + top) / 2, 0.135, 0.135, top - bottom, 0.19, m.darkWood);
      for (const yy of [bottom + 0.22, top - 0.14]) {
        const peg = mesh(group, ctx.kit.shared('timber-house:peg', () => new THREE.CylinderGeometry(0.021, 0.021, 0.022, 6)), m.wood, xx, yy, 0.241);
        peg.rotation.x = Math.PI / 2;
      }
    }
    box(group, 0, 3.96, 0.145, innerPost * 2, 0.12, 0.18, m.darkWood);
    for (const side of [-1, 1]) {
      const brace = mesh(group, side < 0 ? kneeGeometry : reverseKnee, m.darkWood, side * innerPost, top - 0.96, 0.06);
      brace.scale.x = innerPost / 0.98;
      line(group, [side * (innerPost - 0.06), bottom + 0.22, 0.15], [side * 0.09, 3.72, 0.15], 0.11, m.darkWood);
    }
    const count = Math.floor(length / 0.5);
    for (let i = 0; i < count; i += 1) box(group, (i - (count - 1) / 2) * 0.5, bottom + 0.015, 0.19, 0.13, 0.13, 0.3, m.wood);
    return group;
  };
  face(x, z + depth / 2, width - 0.12, 0, 0.98, width / 2 - 0.28);
  const side = face(x + width / 2, z, depth - 0.12, Math.PI / 2, 0.46, depth / 2 - 0.17);
  for (const direction of [-1, 1]) line(side, [0, height + 0.08, 0.1], [direction * 0.96, height + 0.73, 0.1], 0.12, m.darkWood);
  return root;
};
